import React, { useState } from 'react';
import { camelCase } from 'lodash';
import styled from 'styled-components';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { ConfigFieldControl, ConfigTextField, StyledStatusLabel } from '@sierralabs/core-ui';
import { useEntityContext } from '@sierralabs/entity-ui';
// import { useQueryContext } from '@sierralabs/query-ui';
import SiteQueryFieldPicker from '../../common/pickers/SiteQueryFieldPicker';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useQueryDisplayValue } from '../../hooks/component/useQueryDisplayValue';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
import { useGetQueriesForComponentTree } from '../../hooks/component/useGetQueriesForComponentTree';
function getDefaultEmptyChip() {
    return { value: 'Null/Empty', label: 'N/A', borderRadius: '6px' };
}
export const SiteChip = props => {
    var _a, _b, _c;
    const component = useSubscribeComponentChanges(SiteChipPlugin, props);
    const { queryId, queryField } = component.props || {};
    const { isHover, hoverProps } = useSiteContext();
    const value = useQueryDisplayValue(queryId, queryField, props.queryValueProps);
    const classes = ['site-chip'];
    if (isHover(component)) {
        classes.push('component-hover');
    }
    // use default empty chip when not configured
    const chip = value
        ? (_b = (_a = component.props) === null || _a === void 0 ? void 0 : _a.chips) === null || _b === void 0 ? void 0 : _b.find(chip => (chip === null || chip === void 0 ? void 0 : chip.value) === value)
        : ((_c = component.props) === null || _c === void 0 ? void 0 : _c.emptyChip) || getDefaultEmptyChip();
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteChipPlugin }, props),
        React.createElement(StyledStatusLabel, Object.assign({}, hoverProps(component), { className: classes.join(' '), style: {
                color: chip === null || chip === void 0 ? void 0 : chip.color,
                borderRadius: chip === null || chip === void 0 ? void 0 : chip.borderRadius,
                backgroundColor: chip === null || chip === void 0 ? void 0 : chip.backgroundColor
            } }), (chip === null || chip === void 0 ? void 0 : chip.label) || value)));
};
const StyledDiv = styled.div `
  .header {
    padding: 8px 8px 0;
  }
  .content {
    padding: 8px;
  }
  .MuiAccordion-root {
    margin-left: -12px;
    margin-right: -12px;
    &.Mui-expanded {
      margin-top: 0;
      margin-bottom: 0;
    }
  }
  .MuiAccordionSummary-root.Mui-expanded {
    min-height: 48px;
  }
  .MuiAccordionSummary-root,
  .MuiAccordionDetails-root {
    padding-left: 12px;
    padding-right: 12px;
  }
`;
export const SiteChipConfigurator = props => {
    var _a, _b, _c;
    const { entities } = useEntityContext();
    const { component, update } = useConfigComponent(props.component);
    const queries = useGetQueriesForComponentTree(component);
    const [expandedItem, setExpandedItem] = useState();
    // const { queries } = useQueryContext();
    const { queryId, queryField } = component.props || {};
    const query = queries.find(query => query.id === queryId);
    const entity = entities === null || entities === void 0 ? void 0 : entities.find(entity => { var _a, _b; return entity.tableName === ((_b = (_a = query === null || query === void 0 ? void 0 : query.config) === null || _a === void 0 ? void 0 : _a.mainAlias) === null || _b === void 0 ? void 0 : _b.tablePath); });
    const column = entity === null || entity === void 0 ? void 0 : entity.columns.find(column => {
        const name = column.name || camelCase(column.displayName);
        const property = (queryField === null || queryField === void 0 ? void 0 : queryField.includes('.')) ? queryField.split('.')[1] : '';
        return name === property;
    });
    const isEnum = column && column.type === 'enum';
    const items = isEnum && Array.isArray(column.enum) ? column.enum : [];
    const emptyChip = (_a = component.props) === null || _a === void 0 ? void 0 : _a.emptyChip;
    // use default empty chip to properly show default values
    const emptyChipItem = emptyChip || getDefaultEmptyChip();
    const handleChange = (item, index, property, value) => {
        var _a;
        const chipsPath = 'props.chips';
        const chips = (_a = component.props) === null || _a === void 0 ? void 0 : _a.chips;
        if (!chips) {
            update(chipsPath, []);
        }
        const chip = chips === null || chips === void 0 ? void 0 : chips[index];
        const path = `${chipsPath}[${index}]`;
        if (!chip) {
            update(path, { value: item, borderRadius: '6px' });
        }
        else if (chip.value !== item) {
            update(`${path}.value`, item);
        }
        update(`${path}.${property}`, value);
    };
    return (React.createElement(StyledDiv, null,
        React.createElement(ConfigFieldControl, null,
            React.createElement(SiteQueryFieldPicker, { queryId: (_b = component.props) === null || _b === void 0 ? void 0 : _b.queryId, queryField: (_c = component.props) === null || _c === void 0 ? void 0 : _c.queryField, options: queries, onChange: value => {
                    update('props.queryId', value.queryId);
                    update('props.queryField', value.queryField);
                } })),
        React.createElement(Box, { my: 1 }, items.length === 0 ? (React.createElement(Typography, { color: 'textSecondary' }, "No enum items configured.")) : (React.createElement(Typography, { variant: 'overline', color: 'textSecondary' }, "Enum Items"))),
        items.map((item, index) => {
            var _a, _b;
            const chip = (_b = (_a = component.props) === null || _a === void 0 ? void 0 : _a.chips) === null || _b === void 0 ? void 0 : _b[index];
            return (React.createElement(ChipItemConfig, { key: `${item}-${index}`, item: item, chip: chip, expanded: item === expandedItem, onChange: (property, value) => {
                    handleChange(item, index, property, value);
                }, setExpanded: expanded => {
                    setExpandedItem(expanded ? item : undefined);
                } }));
        }),
        isEnum && (React.createElement(ChipItemConfig, { item: emptyChipItem.value, chip: emptyChipItem, expanded: typeof expandedItem === 'object' && expandedItem.emptyChip, onChange: (property, value) => {
                const basePath = 'props.emptyChip';
                if (!emptyChip) {
                    update(basePath, getDefaultEmptyChip());
                }
                update(`${basePath}.${property}`, value);
            }, setExpanded: expanded => {
                setExpandedItem(expanded ? { emptyChip: true } : undefined);
            } }))));
};
const ChipItemConfig = props => {
    const { item, expanded, chip, onChange, setExpanded } = props;
    return (React.createElement(Accordion, { expanded: expanded, onChange: (_, expanded) => setExpanded === null || setExpanded === void 0 ? void 0 : setExpanded(expanded) },
        React.createElement(AccordionSummary, { expandIcon: React.createElement(ExpandMore, null) },
            React.createElement(Typography, { variant: 'body2' }, item)),
        React.createElement(AccordionDetails, null,
            React.createElement("div", null,
                React.createElement(ConfigFieldControl, null,
                    React.createElement(ConfigTextField, { fullWidth: true, label: 'Label', value: (chip === null || chip === void 0 ? void 0 : chip.label) || '', onChange: event => {
                            onChange('label', event.target.value);
                        } })),
                React.createElement(ConfigFieldControl, null,
                    React.createElement(ConfigTextField, { fullWidth: true, label: 'Border Radius', value: (chip === null || chip === void 0 ? void 0 : chip.borderRadius) || '6px', onChange: event => {
                            onChange('borderRadius', event.target.value);
                        } })),
                React.createElement(ConfigFieldControl, null,
                    React.createElement(ConfigTextField, { fullWidth: true, label: 'Color', value: (chip === null || chip === void 0 ? void 0 : chip.color) || '', onChange: event => {
                            onChange('color', event.target.value);
                        } })),
                React.createElement(ConfigFieldControl, null,
                    React.createElement(ConfigTextField, { fullWidth: true, label: 'Background Color', value: (chip === null || chip === void 0 ? void 0 : chip.backgroundColor) || '', onChange: event => {
                            onChange('backgroundColor', event.target.value);
                        } }))))));
};
export const SiteChipPlugin = {
    name: 'Chip',
    type: 'chip',
    component: SiteChip,
    configurator: SiteChipConfigurator
};
//# sourceMappingURL=SiteChipPlugin.js.map